/**
 * Filtro de certificados por CPF (#92, D-063 item 2) — lado do browser.
 *
 * O CPF esperado do participante NUNCA sai do servidor: `GET /v1/signers/:id/
 * certificate-expectation` devolve só um token de comparação de mão única
 * (`cpf_match = sha256hex(envelope_id + ':' + cpf)`). Este módulo extrai o CPF do
 * titular de cada certificado que o AGENTE LOCAL enumerou (dado que já está na
 * máquina do próprio usuário), computa o mesmo hash e compara.
 *
 * A extração espelha `extractSubjectCpf` de `apps/api/src/domain/certificate-binding.ts`
 * (convenção ICP-Brasil "NOME:CPF" no CN — DOC-ICP-04/05): os 11 dígitos após o
 * ÚLTIMO ':' do valor do CN. A diferença é a FONTE: lá o CN sai do DER via
 * `node:crypto`; aqui sai do `subject` RFC2253 que o agente local já entrega
 * (`lib/agent.ts` — não há parser de DER no browser, e não precisa haver).
 *
 * ISTO NÃO É AUTORIZAÇÃO — é UX + privacidade. A autoridade do vínculo
 * certificado ↔ signatário continua sendo `checkCertificateBinding` (D-035),
 * server-side, na abertura da sessão. Fail-closed local: qualquer certificado cujo
 * CPF não puder ser extraído é tratado como INCOMPATÍVEL quando há expectativa.
 */
import type { AgentCert } from './agent';
import { sha256HexUtf8 } from './crypto';

/**
 * Divide um DN RFC2253 em componentes ("CN=...", "O=...", ...) respeitando o
 * escape com '\' (uma vírgula escapada '\,' pertence ao VALOR, não separa).
 * Parser mínimo e suficiente para localizar atributos — não tenta normalizar
 * OIDs nem multi-valued RDNs ('+'), que não ocorrem na convenção que lemos.
 */
function splitDnComponents(dn: string): string[] {
  const parts: string[] = [];
  let current = '';
  let escaped = false;
  for (const ch of dn) {
    if (escaped) {
      current += ch;
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      escaped = true;
      continue;
    }
    if (ch === ',') {
      parts.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  parts.push(current);
  return parts.map((part) => part.trim()).filter((part) => part.length > 0);
}

/** Valor do primeiro atributo `attr=` (case-insensitive na CHAVE) de um DN RFC2253, já sem escape. */
export function dnAttribute(dn: string, attr: string): string | null {
  const prefix = `${attr.toUpperCase()}=`;
  const component = splitDnComponents(dn).find((part) => part.toUpperCase().startsWith(prefix));
  if (!component) return null;
  return component.slice(component.indexOf('=') + 1);
}

/**
 * CPF do titular a partir do `subject` RFC2253 do certificado: 11 dígitos após o
 * ÚLTIMO ':' do CN. `null` (fail-closed) quando não há CN, não há ':' ou os
 * dígitos não somam exatamente 11 — espelho fiel de `extractSubjectCpf`.
 */
export function extractCertCpf(subjectDn: string): string | null {
  const cn = dnAttribute(subjectDn, 'CN');
  if (!cn) return null;
  const lastColon = cn.lastIndexOf(':');
  if (lastColon === -1) return null;
  const digits = cn.slice(lastColon + 1).replace(/\D/g, '');
  return digits.length === 11 ? digits : null;
}

/** Nome do titular SEM o CPF: o valor do CN antes do ÚLTIMO ':' (ou o CN inteiro se não houver ':'). */
export function certHolderName(subjectDn: string): string | null {
  const cn = dnAttribute(subjectDn, 'CN');
  if (!cn) return null;
  const lastColon = cn.lastIndexOf(':');
  return (lastColon === -1 ? cn : cn.slice(0, lastColon)).trim() || null;
}

/** Rótulo humano do emissor: CN do issuer, senão O, senão o DN cru (melhor que nada). */
export function issuerLabel(issuerDn: string): string {
  return dnAttribute(issuerDn, 'CN') ?? dnAttribute(issuerDn, 'O') ?? issuerDn;
}

/** O MESMO token de comparação que o servidor computa: sha256hex(`${envelopeId}:${cpf}`). */
export function cpfMatchToken(envelopeId: string, cpfDigits: string): Promise<string> {
  return sha256HexUtf8(`${envelopeId}:${cpfDigits}`);
}

/**
 * `true` sse o CPF do titular do certificado casa com o token `cpf_match` do
 * envelope. Certificado sem CPF extraível NUNCA casa (fail-closed do filtro).
 */
export async function certMatchesExpectation(
  cert: Pick<AgentCert, 'subject'>,
  envelopeId: string,
  cpfMatch: string,
): Promise<boolean> {
  const cpf = extractCertCpf(cert.subject);
  if (!cpf) return false;
  return (await cpfMatchToken(envelopeId, cpf)) === cpfMatch;
}

/**
 * Aplica o filtro #92 à lista do agente. `cpfMatch === null` = sem expectativa a
 * comparar (signatário sem CPF, eliminado, ou modalidade sem certificado) — a
 * lista passa INTEIRA (sem filtro), como manda o contrato da rota.
 * Com expectativa: certificado incompatível NUNCA aparece (nem desabilitado).
 */
export async function filterCertsByExpectation(
  certs: AgentCert[],
  envelopeId: string,
  cpfMatch: string | null,
): Promise<AgentCert[]> {
  if (cpfMatch === null) return certs;
  const verdicts = await Promise.all(
    certs.map((cert) => certMatchesExpectation(cert, envelopeId, cpfMatch)),
  );
  return certs.filter((_, index) => verdicts[index]);
}
